import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Package, ShoppingBag, Users, DollarSign, TrendingUp, Shirt, BarChart3 } from "lucide-react";
import AdminLayout from "../../components/AdminLayout";
import { api } from "../../lib/api";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function absoluteImg(url) {
  if (!url) return "";
  if (url.startsWith("http")) return url;
  if (url.startsWith("/api/")) return `${BACKEND_URL}${url}`;
  return url;
}

function RankBadge({ rank }) {
  const colors = {
    1: "bg-[#CFF048] text-black",
    2: "bg-[#A8C43F] text-black",
    3: "bg-[#7A9B2F] text-black",
  };
  return (
    <span className={`inline-flex items-center justify-center w-6 h-6 text-xs font-black label-mono ${colors[rank] || "bg-[#27272A] text-[#71717A]"}`}>
      {rank}
    </span>
  );
}

export default function AdminDashboard() {
  const [stats, setStats] = useState({ total_orders: 0, total_users: 0, total_products: 0, revenue: 0 });
  const [orders, setOrders] = useState([]);
  const [analytics, setAnalytics] = useState({ top_designs: [], top_sizes: [], top_buyers: [] });
  const [analyticsLoading, setAnalyticsLoading] = useState(true);

  useEffect(() => {
    api.get("/admin/stats").then((r) => setStats(r.data));
    api.get("/admin/orders").then((r) => setOrders(r.data.slice(0, 8)));
    api.get("/admin/analytics")
      .then((r) => setAnalytics(r.data))
      .finally(() => setAnalyticsLoading(false));
  }, []);

  const cards = [
    { label: "REVENUE", value: `₹${stats.revenue.toFixed(0)}`, icon: DollarSign },
    { label: "ORDERS", value: stats.total_orders, icon: ShoppingBag },
    { label: "PRODUCTS", value: stats.total_products, icon: Package },
    { label: "MEMBERS", value: stats.total_users, icon: Users },
  ];

  return (
    <AdminLayout title="Dashboard">
      {/* STATS CARDS */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="admin-stats-grid">
        {cards.map((c) => (
          <div key={c.label} className="bg-[#0A0A0A] border border-[#27272A] p-5">
            <div className="flex justify-between items-start">
              <p className="label-mono text-[#71717A]">{c.label}</p>
              <c.icon size={16} className="text-[#CFF048]" />
            </div>
            <p className="font-display font-black text-3xl mt-3 text-white">{c.value}</p>
          </div>
        ))}
      </div>

      {/* ANALYTICS — 3 TABLES */}
      <div className="mt-8 grid grid-cols-1 xl:grid-cols-3 gap-6">
        {/* TOP 10 DESIGNS */}
        <div className="bg-[#0A0A0A] border border-[#27272A] flex flex-col" data-testid="top-designs-table">
          <div className="px-5 py-4 border-b border-[#27272A] flex items-center gap-2">
            <Shirt size={14} className="text-[#CFF048]" />
            <p className="label-mono text-[#CFF048]">// TOP 10 DESIGNS</p>
            <span className="ml-auto label-mono text-[10px] text-[#71717A]">SORTED BY PCS SOLD</span>
          </div>
          {analyticsLoading ? (
            <div className="p-8 text-center label-mono text-[#71717A] text-xs">LOADING...</div>
          ) : analytics.top_designs.length === 0 ? (
            <div className="p-8 text-center label-mono text-[#71717A] text-xs">// NO PAID ORDERS YET</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-[#27272A]">
                  <tr className="label-mono text-[#71717A] text-[10px]">
                    <th className="text-left p-3 w-8">#</th>
                    <th className="text-left p-3">DESIGN</th>
                    <th className="text-right p-3">PCS</th>
                    <th className="text-right p-3">AMT</th>
                  </tr>
                </thead>
                <tbody>
                  {analytics.top_designs.map((d, i) => (
                    <tr key={d.product_id} className="border-b border-[#111] hover:bg-[#111]">
                      <td className="p-3"><RankBadge rank={i + 1} /></td>
                      <td className="p-3">
                        <div className="flex items-center gap-2">
                          {d.image && (
                            <img src={absoluteImg(d.image)} alt="" className="w-8 h-8 object-cover border border-[#27272A] flex-shrink-0" />
                          )}
                          <span className="font-display font-bold text-xs leading-tight">{d.name || "Unknown"}</span>
                        </div>
                      </td>
                      <td className="p-3 text-right font-mono-cult text-[#CFF048] font-bold">{d.qty}</td>
                      <td className="p-3 text-right font-mono-cult text-[#71717A] text-xs">₹{d.amount.toFixed(0)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* TOP SIZE-WISE SALES */}
        <div className="bg-[#0A0A0A] border border-[#27272A] flex flex-col" data-testid="top-sizes-table">
          <div className="px-5 py-4 border-b border-[#27272A] flex items-center gap-2">
            <BarChart3 size={14} className="text-[#CFF048]" />
            <p className="label-mono text-[#CFF048]">// SIZE-WISE SALES</p>
            <span className="ml-auto label-mono text-[10px] text-[#71717A]">SORTED BY PCS</span>
          </div>
          {analyticsLoading ? (
            <div className="p-8 text-center label-mono text-[#71717A] text-xs">LOADING...</div>
          ) : analytics.top_sizes.length === 0 ? (
            <div className="p-8 text-center label-mono text-[#71717A] text-xs">// NO PAID ORDERS YET</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-[#27272A]">
                  <tr className="label-mono text-[#71717A] text-[10px]">
                    <th className="text-left p-3 w-8">#</th>
                    <th className="text-left p-3">SIZE</th>
                    <th className="text-right p-3">PCS SOLD</th>
                    <th className="text-right p-3">REVENUE</th>
                  </tr>
                </thead>
                <tbody>
                  {analytics.top_sizes.map((s, i) => {
                    const maxQty = analytics.top_sizes[0]?.qty || 1;
                    const pct = Math.round((s.qty / maxQty) * 100);
                    return (
                      <tr key={s.size} className="border-b border-[#111] hover:bg-[#111]">
                        <td className="p-3"><RankBadge rank={i + 1} /></td>
                        <td className="p-3">
                          <div>
                            <span className="font-display font-black text-base text-white">{s.size}</span>
                            <div className="mt-1 h-1 bg-[#111] w-full">
                              <div className="h-1 bg-[#CFF048]" style={{ width: `${pct}%` }} />
                            </div>
                          </div>
                        </td>
                        <td className="p-3 text-right font-mono-cult text-[#CFF048] font-bold">{s.qty}</td>
                        <td className="p-3 text-right font-mono-cult text-[#71717A] text-xs">₹{s.amount.toFixed(0)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* TOP 10 BUYERS */}
        <div className="bg-[#0A0A0A] border border-[#27272A] flex flex-col" data-testid="top-buyers-table">
          <div className="px-5 py-4 border-b border-[#27272A] flex items-center gap-2">
            <TrendingUp size={14} className="text-[#CFF048]" />
            <p className="label-mono text-[#CFF048]">// TOP 10 BUYERS</p>
            <span className="ml-auto label-mono text-[10px] text-[#71717A]">BY SPEND</span>
          </div>
          {analyticsLoading ? (
            <div className="p-8 text-center label-mono text-[#71717A] text-xs">LOADING...</div>
          ) : analytics.top_buyers.length === 0 ? (
            <div className="p-8 text-center label-mono text-[#71717A] text-xs">// NO PAID ORDERS YET</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-[#27272A]">
                  <tr className="label-mono text-[#71717A] text-[10px]">
                    <th className="text-left p-3 w-8">#</th>
                    <th className="text-left p-3">BUYER</th>
                    <th className="text-right p-3">ORDERS</th>
                    <th className="text-right p-3">SPENT</th>
                  </tr>
                </thead>
                <tbody>
                  {analytics.top_buyers.map((b, i) => (
                    <tr key={b.email} className="border-b border-[#111] hover:bg-[#111]">
                      <td className="p-3"><RankBadge rank={i + 1} /></td>
                      <td className="p-3">
                        <p className="font-display font-bold text-xs truncate max-w-[120px]">{b.name || b.email}</p>
                        <p className="label-mono text-[#71717A] text-[10px] truncate max-w-[120px]">{b.email}</p>
                      </td>
                      <td className="p-3 text-right font-mono-cult text-[#71717A] text-xs">{b.orders}</td>
                      <td className="p-3 text-right font-mono-cult text-[#CFF048] font-bold text-xs">₹{b.amount.toFixed(0)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* RECENT ORDERS */}
      <div className="mt-6 bg-[#0A0A0A] border border-[#27272A]">
        <div className="px-5 py-4 border-b border-[#27272A] flex justify-between items-center">
          <p className="label-mono text-[#CFF048]">// RECENT ORDERS</p>
          <Link to="/admin/orders" className="label-mono text-[#71717A] hover:text-white text-xs">VIEW ALL →</Link>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="admin-recent-orders-table">
            <thead className="border-b border-[#27272A]">
              <tr className="label-mono text-[#71717A] text-[10px]">
                <th className="text-left p-4">ORDER ID</th>
                <th className="text-left p-4">CUSTOMER</th>
                <th className="text-left p-4">ITEMS</th>
                <th className="text-left p-4">TOTAL</th>
                <th className="text-left p-4">STATUS</th>
                <th className="text-left p-4">DATE</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((o) => (
                <tr key={o.id} className="border-b border-[#18181B] hover:bg-[#111]">
                  <td className="p-4 font-mono-cult text-[#CFF048]">{o.id.slice(0, 8).toUpperCase()}</td>
                  <td className="p-4 text-xs">{o.user_email}</td>
                  <td className="p-4 font-mono-cult">{o.items.length}</td>
                  <td className="p-4 font-mono-cult">₹{o.total.toFixed(0)}</td>
                  <td className="p-4 label-mono text-[#CFF048] text-[10px]">{o.status.toUpperCase()}</td>
                  <td className="p-4 font-mono-cult text-[#71717A] text-xs">{new Date(o.created_at).toLocaleDateString()}</td>
                </tr>
              ))}
              {orders.length === 0 && (
                <tr><td colSpan={6} className="p-8 text-center label-mono text-[#71717A]">// NO ORDERS YET</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </AdminLayout>
  );
}
